import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
export default function Branches() {
  const { isOrgAdmin } = useAuth();
  const { companies, singleCompany } = useCompany() || {};
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/branches");
      setBranches(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const companyName = (id) => companies?.find((c) => c.id === id)?.name || "—";

  const toggleActive = async (branch) => {
    try {
      await api.patch(`/masters/branches/${branch.id}`, { is_active: !branch.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (branch) => {
    if (!await confirmAction(`Delete branch ${branch.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/masters/branches/${branch.id}`);
      toast.success(`${branch.name} deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      <PageHeader
        title="Branches"
        description={singleCompany ? "Your offices and sites." : "Branch offices or sites under each company, filterable by company."}
        action={isOrgAdmin && <BranchDialog onSaved={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && branches.length === 0 ? (
          <EmptyState title="No branches yet" description={singleCompany ? "Create your first branch: an office or a site." : "Add a company first, then create its branches here."} testId="branches-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead>{!singleCompany && <TableHead>Company</TableHead>}<TableHead>Address</TableHead><TableHead>Phone / Email</TableHead><TableHead>Status</TableHead>{isOrgAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow>
            </TableHeader>
            <TableBody>
              {branches.map((b) => (
                <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{b.code}</TableCell>
                  <TableCell className="text-foreground">{b.name}</TableCell>
                  {!singleCompany && <TableCell className="text-muted-foreground">{companyName(b.company_id)}</TableCell>}
                  <TableCell className="max-w-[260px] text-muted-foreground">
                    <div className="whitespace-pre-line">{b.address || "—"}</div>
                    {b.state && <div className="text-xs">{b.state}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <div>{b.phone || "—"}</div>
                    {b.email && <div className="text-xs">{b.email}</div>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={b.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(b)} data-testid={`branch-active-toggle-${b.code}`} />
                      <Badge variant={b.is_active ? "default" : "secondary"}>{b.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  {isOrgAdmin && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <AssignExistingDialog branch={b} />
                        <BranchDialog branch={b} onSaved={load} />
                        <Button variant="ghost" size="icon" aria-label={`Delete ${b.name}`} onClick={() => remove(b)} data-testid={`branch-delete-${b.code}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

// One form for both: a new branch (company, code and name are set here) and editing an existing one.
function BranchDialog({ branch, onSaved }) {
  const editing = !!branch;
  const blank = { code: "", name: "", state: "", address: "", phone: "", email: "", company_id: "" };
  const fromBranch = () => ({ ...blank, ...Object.fromEntries(Object.entries(branch || {}).map(([k, v]) => [k, v ?? ""])) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(editing ? fromBranch() : blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onOpenChange = (next) => {
    if (next) { setForm(editing ? fromBranch() : blank); setErrors({}); }
    setOpen(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    const details = {
      name: form.name.trim(), state: form.state.trim() || null, address: form.address.trim() || null,
      phone: form.phone.trim() || null, email: form.email.trim() || null,
    };
    try {
      if (editing) {
        await api.patch(`/masters/branches/${branch.id}`, details);
        toast.success(`${details.name} saved`);
      } else {
        await api.post("/masters/branches", { ...details, code: form.code, company_id: form.company_id });
        toast.success(`Branch ${details.name} created`);
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {editing
          ? <Button variant="ghost" size="icon" aria-label={`Edit ${branch.name}`} data-testid={`branch-edit-${branch.code}`}><Pencil className="h-4 w-4" /></Button>
          : <Button data-testid="create-branch-button"><Plus className="h-4 w-4" /> New Branch</Button>}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? `Edit ${branch.name}` : "New Branch"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid={editing ? "edit-branch-form" : "create-branch-form"}>
          {!editing && <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="branch-company-select" placeholder="Select company" />}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input placeholder="Automatic if blank" data-testid="branch-code-input" value={form.code} onChange={set("code")} disabled={editing} className="mt-1.5" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="branch-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="Head Office" />
              <FieldError error={errors.name} />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="branch-address-input" value={form.address} onChange={set("address")} className="mt-1.5" rows={2} placeholder="Street, city, PIN" />
            <FieldError error={errors.address} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>State</Label>
              <Input data-testid="branch-state-input" value={form.state} onChange={set("state")} className="mt-1.5" placeholder="Tamil Nadu" />
              <FieldError error={errors.state} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="branch-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5" placeholder="+91 98765 43210" />
              <FieldError error={errors.phone} />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" data-testid="branch-email-input" value={form.email} onChange={set("email")} className="mt-1.5" placeholder="branch@company.com" />
            <FieldError error={errors.email} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || (!editing && !form.company_id)} data-testid="create-branch-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Records made before branches existed have no branch. This files all of them under the chosen branch, once.
function AssignExistingDialog({ branch }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);

  const onOpenChange = async (next) => {
    setOpen(next);
    if (!next) return;
    setPreview(null);
    try { setPreview((await api.get(`/masters/branches/${branch.id}/unassigned`)).data); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const assign = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/masters/branches/${branch.id}/assign-unassigned`);
      toast.success(`${data.assigned} records filed under ${branch.name}`);
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`branch-assign-${branch.code}`}>File old records here</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>File old records under {branch.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Records made before branches existed have no branch. This puts all of them under {branch.name}. Records that already have a branch are not moved.</p>
        {!preview ? <Loader2 className="h-4 w-4 animate-spin" /> : preview.total === 0 ? (
          <p className="text-sm" data-testid="assign-nothing">Nothing to file. Every record already has a branch.</p>
        ) : (
          <div className="max-h-60 overflow-y-auto rounded-lg border border-border text-sm">
            {preview.tables.map((t) => (
              <div key={t.table} className="flex justify-between border-b border-border px-3 py-1.5 last:border-0"><span>{t.table.replace(/_/g, " ")}</span><span className="font-mono">{t.records}</span></div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={assign} disabled={working || !preview || preview.total === 0} data-testid="assign-confirm-button">
            {working && <Loader2 className="h-4 w-4 animate-spin" />} File {preview?.total || 0} records here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
